import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { InvoiceData } from '../models/invoice-data.interface';
import { BasePdfTemplate } from './base-template';
import { getItemUnitName, getSelectedMappingLines } from './helpers/item-display.helper';

export class HsncodeThermalTemplate extends BasePdfTemplate {
    override templateName = 'hsncode-thermal';
    override templateVersion = '1.0.0';
    override description = 'Custom Thermal Template matching HSN Code format (80mm)';
    override pageSize: 'THERMAL' = 'THERMAL';

    override generatePdfDefinition(data: InvoiceData): TDocumentDefinitions {
        const storename = JSON.parse(localStorage.getItem('storename') || '{}');
        let storeAddress1 = '';
        let storeAddress2 = '';
        console.log(data)
        if (storename) {
            storeAddress1 = [storename.address, storename.address1].filter((p) => p && p.trim()).join(', ');
            storeAddress2 = storename.address2 || '';
        }

        const businessName = storename?.name || data.businessName || '';
        const addr1 = storeAddress1 || '';
        const addr2 = storeAddress2 || '';
        const cityPincode = `${storename?.city || ''} - ${storename?.pincode || ''}`;
        const businessPhone = storename?.mobile || data.businessPhone || '';
        const businessGSTIN = data.businessGSTIN || '';
        const businessFssai = storename?.fssai || ''; // Provide fallback based on image

        const formatDate = this.formatDate.bind(this);
        const invoice = data.items || [];

        const rawDateStr = data.invoiceDate || data.invoicedate;
        let dateObj = data.createdAt ? new Date(data.createdAt) : new Date();
        if (isNaN(dateObj.getTime())) {
            dateObj = new Date();
        }
        let hours = dateObj.getHours();
        const minutes = dateObj.getMinutes();
        const seconds = dateObj.getSeconds();
        const ampm = hours >= 12 ? 'pm' : 'am';
        hours = hours % 12;
        hours = hours ? hours : 12;
        const strTime = hours.toString().padStart(2, '0') + ':' + minutes.toString().padStart(2, '0') + ':' + seconds.toString().padStart(2, '0') + ' ' + ampm;
        const formattedDate = formatDate((data.invoiceDate || data.invoicedate || '') as string | Date);

        let totalDiscount = (data.itemDiscount || 0) + (data.overallDiscount || 0) + (Number(data.loyaltyAmount) || 0);

        let totalCgst = 0;
        let totalSgst = 0;
        // invoice.forEach((item: any) => {
        //     totalCgst += (item.cgstval || 0);
        //     totalSgst += (item.sgstval || 0);
        // });
        // Free items (is_free === 1) are excluded from the item-level CGST/SGST sums
        invoice.forEach((item: any) => {
            if (Number(item.is_free) === 1) return;
            totalCgst += (item.cgstval || 0);
            totalSgst += (item.sgstval || 0);
        });

        if (totalCgst === 0 && data.cgst) totalCgst = data.cgst;
        if (totalSgst === 0 && data.sgst) totalSgst = data.sgst;

        const totalTax = totalCgst + totalSgst + (data.igst || 0);
        // const subtotal = data.subtotal || invoice.reduce((sum: number, item: any) => sum + ((item.quantity || 1) * (item.price || 0)), 0);
        // Fallback sum (used only when backend subtotal is missing) excludes free items
        // const subtotal = data.subtotal || invoice.reduce((sum: number, item: any) => sum + (Number(item.is_free) === 1 ? 0 : ((item.quantity || 1) * (item.price || 0))), 0);
        // Backend subtotal includes free item amount — subtract it so "Total :" excludes free items
        const freeItemsAmount = invoice.reduce((sum: number, item: any) =>
            Number(item.is_free) === 1 ? sum + Number(item.amount || (item.quantity || 1) * (item.price || 0)) : sum, 0);
        const subtotal = data.subtotal
            ? Math.max(0, Number(data.subtotal) - freeItemsAmount)
            : invoice.reduce((sum: number, item: any) => sum + (Number(item.is_free) === 1 ? 0 : ((item.quantity || 1) * (item.price || 0))), 0);
        // rawTotal is built from the free-excluded fallbacks above (subtotal / CGST / SGST)
        const rawTotal = subtotal + totalTax - totalDiscount;

        // Free items summary (is_free === 1): total free qty and free value (MRP/original price x qty)
        let totalFreeQty = 0;
        let totalFreeAmount = 0;
        invoice.forEach((item: any) => {
            if (Number(item.is_free) === 1) {
                totalFreeQty += Number(item.quantity || 0);
                totalFreeAmount += Number(item.originalprice || item.mrp || 0) * Number(item.quantity || 0);
            }
        });
        const netTotal = data.totalAmount || data.grandTotal || Math.round(rawTotal);
        const receivedAmount = data.paidamount || 0; // The image shows 0.00
        const balanceAmount = data.balanceamount || 0; // Image shows negative

        const getSolidLine = (): any => ({
            canvas: [{ type: 'line', x1: 0, y1: 0, x2: 210.5, y2: 0, lineWidth: 1 }],
            margin: [0, 4, 0, 4]
        });

        // Calculate dynamic height to perfectly fit the content and eliminate white space
        // Since item names can wrap, we calculate the estimated lines per item.
        let itemsHeight = 0;
        invoice.forEach((item: any) => {
            const nameLen = (item.name || 'Item Name').length;
            // The name column is ~82pt wide. At 9pt bold, ~14 chars fit per line.
            const lines = Math.ceil(nameLen / 14) || 1;
            // Item row base height + HSN row
            itemsHeight += 18 + (lines * 11);
        });
        // Base height for header, customer, summary, and footer is ~360pt.
        const calculatedHeight = 360 + itemsHeight;

        return {
            pageSize: this.getPageSize(),
            pageOrientation: 'portrait',
            pageMargins: [10, 10, 10, 10],
            // defaultStyle: {
            //     font: 'Roboto',
            //     color: '#000000' // True black for a bolder, sharper look
            // },
            content: [
                // ===== HEADER =====
                {
                    text: `${businessName}`,
                    fontSize: 16,
                    bold: true,
                    alignment: 'center',
                    margin: [0, 0, 0, 3]
                },
                {
                    text: `${addr1} ${addr2}`,
                    fontSize: 10,
                    bold: true,
                    alignment: 'center',
                    margin: [0, 0, 0, 0]
                },
                {
                    text: `${cityPincode}`,
                    fontSize: 9,
                    bold: true,
                    alignment: 'center',
                    margin: [0, 0, 0, 0]
                },
                {
                    text: `Ph : ${businessPhone}`,
                    fontSize: 9,
                    bold: true,
                    alignment: 'center',
                    margin: [0, 0, 0, 0]
                },
                {
                    text: `GSTIN : ${businessGSTIN}`,
                    fontSize: 9,
                    bold: true,
                    alignment: 'center',
                    margin: [0, 0, 0, 0]
                },
                {
                    text: `FSSAI : -${businessFssai}`,
                    fontSize: 9,
                    bold: true,
                    alignment: 'center',
                    margin: [0, 0, 0, 2]
                },

                // ===== ORDER INFO =====
                {
                    columns: [
                        {
                            width: '50%',
                            text: `Invoice No : ${data.invoiceNumber || '2707'}`, fontSize: 9, bold: true
                        },
                        {
                            width: '50%',
                            stack: [
                                {
                                    text: `Date : ${formattedDate}`, fontSize: 9, bold: true, alignment: 'right'
                                },
                                {
                                    text: `Time : ${strTime}`, fontSize: 9, bold: true, alignment: 'right', margin: [0, 2, 0, 0]
                                }
                            ]
                        }
                    ],
                    margin: [0, 0, 0, 4]
                },

                // ===== CUSTOMER INFO (Conditional) =====
                ((data.customerName || data.customerMobile) ? [
                    getSolidLine(),
                    {
                        columns: [
                            {
                                width: '50%',
                                text: data.customerName ? `${data.customerName}` : '',
                                fontSize: 9,
                                bold: true,
                                alignment: 'left'
                            },
                            {
                                width: '50%',
                                text: data.customerMobile ? `Ph No : ${data.customerMobile}` : '',
                                fontSize: 9,
                                bold: true,
                                alignment: 'right'
                            }
                        ],
                        margin: [0, 0, 0, 4]
                    }
                ] : []),

                // ===== ITEMS HEADER =====
                getSolidLine(),
                {
                    columns: [
                        { width: '*', text: 'ITEMS', bold: true, fontSize: 10 },
                        { width: 28, text: 'QTY', bold: true, fontSize: 10, alignment: 'center' },
                        { width: 34, text: 'RATE', bold: true, fontSize: 10, alignment: 'right' },
                        { width: 48, text: 'AMOUNT', bold: true, fontSize: 10, alignment: 'right' }
                    ],
                    margin: [0, 2, 0, 2]
                },
                getSolidLine(),

                // ===== ITEMS =====
                invoice.flatMap((item: any) => {
                    const qty = item.quantity || 1;
                    const rate = item.price || 0;
                    // Subtotal for this line is quantity * rate.
                    // Tax is handled in summary generally. In image it shows 123.81 for 2x65 which is weird.
                    // It seems the item 'RATE' in image is tax-included, or AMOUNT is tax-included?
                    // If rate=65, qty=2, AMOUNT=123.81, wait, 2*65=130. 123.81 + 3.10 + 3.10 = 130!
                    // So the table shows RATE as MRP (65.00) but AMOUNT as Taxable Value (123.81).
                    // And CGST 3.10 SGST 3.10 adds up to 130.00.

                    const amount = item.total || (qty * rate); // We will use item.total if present
                    // const cgstVal = item.cgstval || 0;
                    // const sgstVal = item.sgstval || 0;
                    // Free items print zero CGST/SGST
                    const cgstVal = Number(item.is_free) === 1 ? 0 : (item.cgstval || 0);
                    const sgstVal = Number(item.is_free) === 1 ? 0 : (item.sgstval || 0);

                    return [
                        {
                            columns: [
                                { width: '*', text: `${item.name || 'Item Name'}${Number(item.is_free) === 1 ? ' [FREE]' : ''}`.toUpperCase(), fontSize: 9, bold: true },
                                { width: 28, text: qty.toFixed(2), fontSize: 9, alignment: 'center', bold: true },
                                { width: '*', text: rate.toFixed(2), fontSize: 9, alignment: 'right', bold: true },
                                { width: '48', text: Number(item.is_free) === 1 ? '0.00' : amount.toFixed(2), fontSize: 9, alignment: 'right', bold: true }
                            ],
                            margin: [0, 0, 0, 2]
                        },
                        // Selected variants / parameters below the item name — "label : value",
                        // one per line, only when both label and value are present
                        ...getSelectedMappingLines(item).map((line: string) => ({
                            text: line,
                            fontSize: 7,
                            margin: [0, 0, 0, 1]
                        })),
                        {
                            columns: [
                                // { width: 'auto', text: `HSN Code  ${item.hsncode || item.hsn || ''}`, fontSize: 7, margin: [10, 0, 0, 0] },
                                // Unit shown on the HSN sub-line (qty column is too narrow for it)
                                { width: 'auto', text: `HSN Code  ${item.hsncode || item.hsn || ''}${getItemUnitName(item) ? `   Unit : ${getItemUnitName(item)}` : ''}`, fontSize: 7, margin: [10, 0, 0, 0] },
                                { width: '*', text: `CGST     ${cgstVal.toFixed(2)}      SGST     ${sgstVal.toFixed(2)}`, fontSize: 7, alignment: 'right' }
                            ],
                            margin: [0, 0, 0, 6]
                        }
                    ];
                }),

                getSolidLine(),

                // ===== SUMMARY (Part 1: Subtotals) =====
                {
                    columns: [
                        {
                            width: '45%',
                            // text: `Items : ${invoice.length}   Qty : ${invoice.reduce((sum: number, item: any) => sum + (Number(item.quantity) || 1), 0)}`, fontSize: 9
                            // text: `Items : ${invoice.length}   Qty : ${invoice.reduce((sum: number, item: any) => sum + (Number(item.is_free) === 1 ? 0 : (Number(item.quantity) || 1)), 0)}`, fontSize: 9
                            // T.Qty includes free qty; only amounts exclude free items
                            text: `Items : ${invoice.length}   Qty : ${invoice.reduce((sum: number, item: any) => sum + (Number(item.quantity) || 1), 0)}`, fontSize: 9
                        },
                        {
                            width: '55%',
                            layout: 'noBorders',
                            table: {
                                widths: ['*', 60],
                                body: [
                                    [
                                        { text: 'Total :', fontSize: 9, alignment: 'right', bold: true },
                                        { text: subtotal.toFixed(2), fontSize: 9, alignment: 'right' }
                                    ],
                                    [
                                        { text: 'Round Off :', fontSize: 9, alignment: 'right', bold: false },
                                        { text: data.roundOffAmount?.toFixed(2), fontSize: 9, alignment: 'right' }
                                    ],
                                    // Multiple discount rows logic
                                    ...((totalDiscount > 0) ? [[
                                        { text: 'Discount :', fontSize: 9, alignment: 'right' },
                                        { text: totalDiscount.toFixed(2), fontSize: 9, alignment: 'right' }
                                    ]] : []),
                                    [
                                        { text: 'CGST Tax. :', fontSize: 9, alignment: 'right' },
                                        { text: totalCgst.toFixed(2), fontSize: 9, alignment: 'right' }
                                    ],
                                    [
                                        { text: 'SGST tax. :', fontSize: 9, alignment: 'right' },
                                        { text: totalSgst.toFixed(2), fontSize: 9, alignment: 'right' }
                                    ]
                                ]
                            }
                        }
                    ],
                    margin: [0, 0, 0, 4]
                },

                // ===== FREE ITEMS (only when free items exist) =====
                ...(totalFreeQty > 0 ? [{
                    columns: [
                        { width: '*', text: `Free Items : ${totalFreeQty}`, fontSize: 9, bold: true },
                        { width: 60, text: totalFreeAmount.toFixed(2), fontSize: 9, alignment: 'right', bold: true }
                    ],
                    margin: [0, 0, 0, 4]
                }] : []),

                getSolidLine(),

                // ===== SUMMARY (Part 2: NET TOTAL) =====
                {
                    layout: 'noBorders',
                    table: {
                        widths: ['*', 60],
                        body: [
                            [
                                { text: 'NET TOTAL :', fontSize: 11, alignment: 'right', bold: true },
                                { text: netTotal.toFixed(2), fontSize: 11, alignment: 'right', bold: true }
                            ]
                        ]
                    },
                    margin: [0, 2, 0, 2]
                },

                getSolidLine(),

                // ===== SUMMARY (Part 3: Received & Balance) =====
                {
                    layout: 'noBorders',
                    table: {
                        widths: ['*', 60],
                        body: [
                            [
                                { text: 'Received. Amt. :', fontSize: 9, alignment: 'right' },
                                { text: receivedAmount.toFixed(2), fontSize: 9, alignment: 'right' }
                            ],
                            [
                                { text: 'Balance Amt. :', fontSize: 9, alignment: 'right' },
                                { text: balanceAmount.toFixed(2), fontSize: 9, alignment: 'right' }
                            ]
                        ]
                    },
                    margin: [0, 2, 0, 4]
                },

                getSolidLine(),

                // ===== FOOTER =====
                {
                    text: "Goods Sold Can't Be Exchanged/returned",
                    fontSize: 9,
                    bold: true,
                    alignment: 'center',
                    margin: [0, 15, 0, 5]
                },
                {
                    text: '*** Thank You Visit Again ***',
                    alignment: 'center',
                    fontSize: 9,
                    bold: true,
                    margin: [0, 2, 0, 0]
                }
            ]
        };
    }
}
